import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertCircle, ArrowDown, ArrowUp, CircleSlash2, Copy, Ellipsis, Lock, Play, Trash2 } from 'lucide-react';
import Fieldset from 'components/_UI/Fieldset';
import BatchTimeline from './BatchTimeline';
import TicketOptions from './TicketOptions';
import {
    DESCRIPTION_MAX,
    NAME_MAX,
    OPTION_FIELDS,
    isHalfTicket,
    isTicketFree,
    ticketCapacity,
    ticketCommitted,
} from './ticketModel';
import { FieldError, FieldHint, INK, INK_SOFT, IconButton, LINE, MUTED, OutlineButton, accent, visuallyHidden } from './ticketFormStyles';

// Com 255 de limite, contar desde o primeiro caractere é ruído: o contador só
// aparece quando o nome se aproxima do fim.
const NAME_HINT_FROM = NAME_MAX - 40;

/**
 * Um ingresso por vez: o essencial à vista (nome, descrição, lotes) e o resto
 * em "Mais opções".
 *
 * O erro de um campo só aparece depois que o produtor mexe nele ou tenta
 * salvar — um ingresso recém-criado não nasce todo vermelho.
 */
export default function TicketEditor({
    ticket,
    onChange,
    isEventFree,
    absorbed,
    feeFor,
    issues,
    submitAttempted,
    canMoveUp,
    canMoveDown,
    canDelete,
    onMoveUp,
    onMoveDown,
    onDuplicate,
    onCreateHalf,
    onDelete,
    onDeleteBatchId,
    revealKey,
}) {
    const [touched, setTouched] = useState(() => new Set());
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    const free = isTicketFree(ticket, isEventFree);
    const committed = ticketCommitted(ticket);
    const capacity = ticketCapacity(ticket);
    const hasSales = committed > 0;

    useEffect(() => {
        if (!menuOpen) return undefined;

        function closeOnOutside(e) {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        }

        document.addEventListener('mousedown', closeOnOutside);
        return () => document.removeEventListener('mousedown', closeOnOutside);
    }, [menuOpen]);

    function touch(field, lot) {
        setTouched(previous => {
            const key = `${field}|${lot ?? ''}`;
            if (previous.has(key)) return previous;
            const next = new Set(previous);
            next.add(key);
            return next;
        });
    }

    function errorFor(field, lot) {
        const issue = issues.find(item => item.field === field && (lot === undefined || item.lot === lot));

        if (!issue) return '';

        return submitAttempted || touched.has(`${field}|${issue.lot ?? ''}`) ? issue.message : '';
    }

    function setField(field, value) {
        onChange(previous => ({ ...previous, [field]: value }));
    }

    function runMenu(action) {
        setMenuOpen(false);
        action();
    }

    const nameError = errorFor('name');
    const nameLength = ticket.name?.length || 0;
    const descriptionError = errorFor('description');
    const optionError = OPTION_FIELDS.some(field => errorFor(field));

    return (
        <Wrapper aria-labelledby={`editor-title-${ticket.internal_id}`}>
            <Hidden id={`editor-title-${ticket.internal_id}`}>
                Editar ingresso {ticket.name || 'sem nome'}
            </Hidden>

            <div>
                <Top>
                    <NameField data-invalid={nameError ? 'true' : undefined}>
                        <Fieldset
                            id={`name-${ticket.internal_id}`}
                            layout='squired'
                            label='Nome do ingresso'
                            placeholder='Ex.: Inteira, Meia-entrada, Camarote'
                            value={ticket.name || ''}
                            syncProp
                            maxLength={NAME_MAX}
                            onChangeValue={value => {
                                setField('name', value);
                                touch('name');
                            }}
                        />
                    </NameField>

                    <Actions>
                        {!free && !isHalfTicket(ticket) && (
                            <OutlineButton type='button' onClick={onCreateHalf} title='Duplica este ingresso com metade do preço em todos os lotes'>
                                <HalfIcon aria-hidden='true' />
                                Criar meia-entrada
                            </OutlineButton>
                        )}
                        <IconButton type='button' aria-label='Duplicar ingresso' title='Duplicar ingresso' onClick={onDuplicate}>
                            <Copy size={18} aria-hidden='true' />
                        </IconButton>
                        <MenuAnchor
                            ref={menuRef}
                            onKeyDown={e => {
                                if (e.key === 'Escape') setMenuOpen(false);
                            }}
                        >
                            <IconButton
                                type='button'
                                aria-label='Mais ações'
                                aria-haspopup='menu'
                                aria-expanded={menuOpen}
                                active={menuOpen}
                                onClick={() => setMenuOpen(value => !value)}
                            >
                                <Ellipsis size={18} aria-hidden='true' />
                            </IconButton>
                            {menuOpen && (
                                <Menu role='menu' aria-label='Ações do ingresso'>
                                    <MenuItem type='button' role='menuitem' disabled={!canMoveUp} onClick={() => runMenu(onMoveUp)}>
                                        <ArrowUp size={16} aria-hidden='true' />
                                        <span>Mover para cima</span>
                                    </MenuItem>
                                    <MenuItem type='button' role='menuitem' disabled={!canMoveDown} onClick={() => runMenu(onMoveDown)}>
                                        <ArrowDown size={16} aria-hidden='true' />
                                        <span>Mover para baixo</span>
                                    </MenuItem>
                                    <MenuDivider />
                                    <MenuItem type='button' role='menuitem' onClick={() => runMenu(() => setField('is_active', !ticket.is_active))}>
                                        {ticket.is_active ? <CircleSlash2 size={16} aria-hidden='true' /> : <Play size={16} aria-hidden='true' />}
                                        <MenuText>
                                            <span>{ticket.is_active ? 'Desativar ingresso' : 'Ativar ingresso'}</span>
                                            <small>{ticket.is_active ? 'Para de vender e mantém o que já foi vendido' : 'Volta a aparecer para venda'}</small>
                                        </MenuText>
                                    </MenuItem>
                                    {hasSales || !canDelete ? (
                                        <MenuItem type='button' role='menuitem' aria-disabled='true' muted>
                                            <Lock size={16} aria-hidden='true' />
                                            <MenuText>
                                                <span>Excluir ingresso</span>
                                                <small>
                                                    {hasSales
                                                        ? `Indisponível: ${committed} já vendidos. Desative em vez de excluir.`
                                                        : 'O evento precisa de pelo menos um ingresso'}
                                                </small>
                                            </MenuText>
                                        </MenuItem>
                                    ) : (
                                        <MenuItem type='button' role='menuitem' danger onClick={() => runMenu(onDelete)}>
                                            <Trash2 size={16} aria-hidden='true' />
                                            <span>Excluir ingresso</span>
                                        </MenuItem>
                                    )}
                                </Menu>
                            )}
                        </MenuAnchor>
                    </Actions>
                    <NameHint>
                        {nameError
                            ? <FieldError><AlertCircle size={12} aria-hidden='true' />{nameError}</FieldError>
                            : nameLength >= NAME_HINT_FROM && <FieldHint>{nameLength}/{NAME_MAX}</FieldHint>}
                    </NameHint>
                </Top>
            </div>

            {hasSales && (
                <SalesLine>
                    <strong>{committed} de {capacity} vendidos</strong> · o desempenho completo fica na aba Ingressos do evento
                </SalesLine>
            )}

            <div data-invalid={descriptionError ? 'true' : undefined}>
                <Fieldset
                    id={`description-${ticket.internal_id}`}
                    layout='squired'
                    label='Descrição (opcional)'
                    placeholder='Ex.: Ao comprar o ingresso ganhe um copo de brinde'
                    value={ticket.description || ''}
                    syncProp
                    onChangeValue={value => {
                        setField('description', value);
                        touch('description');
                    }}
                />
                {descriptionError
                    ? <FieldError><AlertCircle size={12} aria-hidden='true' />{descriptionError}</FieldError>
                    : <FieldHint>{ticket.description?.length || 0}/{DESCRIPTION_MAX} · aparece no checkout, abaixo do nome</FieldHint>}
            </div>

            <BatchTimeline
                ticket={ticket}
                onChange={onChange}
                isEventFree={isEventFree}
                absorbed={absorbed}
                feeFor={feeFor}
                errorFor={errorFor}
                touch={touch}
                onDeleteBatchId={onDeleteBatchId}
            />

            <TicketOptions
                ticket={ticket}
                onChange={onChange}
                errorFor={errorFor}
                touch={touch}
                hasVisibleError={optionError}
                revealKey={revealKey}
            />
        </Wrapper>
    );
}

/* Meio círculo cheio: o ícone de "metade" que o lucide não tem. */
function HalfIcon(props) {
    return (
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' {...props}>
            <circle cx='12' cy='12' r='9' />
            <path d='M12 3a9 9 0 0 0 0 18z' fill='currentColor' stroke='none' />
        </svg>
    );
}

const Wrapper = styled.section`
    background: #fff;
    border: 1px solid ${LINE};
    border-radius: 16px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 22px;

    @media screen and (max-width: 620px) {
        border: 0;
        border-radius: 0;
        padding: 16px 0;
        background: transparent;
    }
`;

const Hidden = styled.h2`
    ${visuallyHidden}
`;

/* Os botões alinham pela base do campo de nome, e o contador fica numa linha
   própria logo abaixo do campo. No celular os botões descem para depois do
   contador, para ele não se separar do campo a que pertence. */
const Top = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
        'field actions'
        'hint .';
    align-items: end;
    column-gap: 12px;

    @media screen and (max-width: 620px) {
        grid-template-columns: minmax(0, 1fr);
        grid-template-areas:
            'field'
            'hint'
            'actions';
        row-gap: 10px;
    }
`;

const NameHint = styled.div`
    grid-area: hint;
`;

const FIELD_HEIGHT = '46px';

const NameField = styled.div`
    grid-area: field;
    min-width: 0;

    /* O Fieldset traz 8px de respiro embaixo; com os botões alinhados pela
       base, esse respiro os deixava abaixo do campo. */
    & > div {
        padding-bottom: 0;
    }

    input {
        height: ${FIELD_HEIGHT};
        box-sizing: border-box;
        font-size: 17px;
        font-weight: 500;
    }
`;

const Actions = styled.div`
    grid-area: actions;
    display: flex;
    gap: 8px;

    & > button,
    & > div > button {
        height: ${FIELD_HEIGHT};
    }

    & > div > button {
        width: ${FIELD_HEIGHT};
    }

    @media screen and (max-width: 620px) {
        & > button:first-child {
            flex: 1 1 auto;
        }
    }
`;

const MenuAnchor = styled.div`
    position: relative;
`;

const Menu = styled.div`
    position: absolute;
    top: 52px;
    right: 0;
    z-index: 20;
    width: 290px;
    padding: 6px 0;
    background: #fff;
    border: 1px solid ${LINE};
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(20, 10, 50, 0.16);
`;

const MenuItem = styled.button.withConfig({
    shouldForwardProp: prop => !['danger', 'muted'].includes(prop),
})`
    width: 100%;
    min-height: 44px;
    padding: 8px 14px;
    display: flex;
    align-items: center;
    gap: 10px;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 14px;
    text-align: left;
    color: ${({ danger, muted }) => (danger ? '#B3261E' : muted ? '#6E6E76' : INK)};
    cursor: ${({ muted }) => (muted ? 'not-allowed' : 'pointer')};

    svg {
        flex-shrink: 0;
    }

    &:disabled {
        color: #9A9AA3;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: ${({ muted }) => (muted ? 'transparent' : '#F6F5FA')};
    }

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: -2px;
    }
`;

const MenuText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 2px;

    small {
        font-size: 12px;
        line-height: 1.35;
        color: ${MUTED};
    }
`;

const MenuDivider = styled.div`
    height: 1px;
    margin: 6px 0;
    background: #ECECF0;
`;

const SalesLine = styled.p`
    margin: -12px 0 0;
    font-size: 13px;
    color: ${INK_SOFT};

    strong {
        color: ${INK};
    }
`;
